import {
  CONCIERGE_ENTERTAINMENT_FALLBACK_IMAGE,
  CONCIERGE_YACHT_FALLBACK_IMAGE,
  getConciergeCatalogImageSrc,
  hasConciergeCatalogImage,
} from '../constants/service-images';
import {
  ConciergeBreadcrumb,
  ConciergeCatalogItem,
  ConciergeCatalogPage,
} from '../model/types';

export type ConciergeItemCatalog = 'excursions' | 'shows' | 'yacht';

export const conciergeItemCatalogPath: Record<
  ConciergeItemCatalog,
  `/${string}`
> = {
  excursions: '/concierge/excursions',
  shows: '/concierge/shows',
  yacht: '/concierge/yacht',
};

export const getConciergeItemHref = (
  catalog: ConciergeItemCatalog,
  slug: string
) => `${conciergeItemCatalogPath[catalog]}/${slug}`;

export const getConciergeItemAssetCollection = (
  catalog: ConciergeItemCatalog
): NonNullable<ConciergeCatalogPage['assetCollection']> =>
  catalog === 'yacht' ? 'yacht' : 'entertainment';

export const getCatalogFallbackImage = (
  assetCollection?: ConciergeCatalogPage['assetCollection']
) => {
  if (assetCollection === 'yacht') {
    return CONCIERGE_YACHT_FALLBACK_IMAGE;
  }

  if (assetCollection === 'entertainment') {
    return CONCIERGE_ENTERTAINMENT_FALLBACK_IMAGE;
  }

  return '';
};

export const getConciergeItemGalleryImageNames = (
  item: ConciergeCatalogItem,
  options?: { skipCover?: boolean }
) => {
  const names = item.modal?.galleryImageNames?.length
    ? [...item.modal.galleryImageNames]
    : item.assetFolder
      ? ['cover']
      : [];

  if (!options?.skipCover) {
    return names;
  }

  return names.length > 1 ? names.slice(1) : [];
};

export const getConciergeItemGallerySrcs = (
  item: ConciergeCatalogItem,
  assetCollection: NonNullable<ConciergeCatalogPage['assetCollection']>,
  options?: { skipCover?: boolean }
) => {
  const imageNames = getConciergeItemGalleryImageNames(item, options);
  const fallback = getCatalogFallbackImage(assetCollection);

  return imageNames
    .map((imageName) => {
      if (!item.assetFolder) {
        return fallback || '';
      }

      const hasImage = hasConciergeCatalogImage({
        collection: assetCollection,
        assetFolder: item.assetFolder,
        imageName,
      });

      if (hasImage) {
        return getConciergeCatalogImageSrc({
          collection: assetCollection,
          assetFolder: item.assetFolder,
          imageName,
        });
      }

      return fallback;
    })
    .filter(Boolean);
};

export const getCatalogListingRobots = (
  pageQuery: string | string[] | number | undefined
) => {
  const raw = Array.isArray(pageQuery) ? pageQuery[0] : pageQuery;
  const page = Number(raw);

  if (!Number.isFinite(page) || page <= 1) {
    return undefined;
  }

  return 'noindex, follow';
};

export const resolveCatalogListingRobots = (
  pageQuery: string | string[] | number | undefined,
  fallback: string | null | undefined,
  isRouterReady = false
) => {
  if (pageQuery !== undefined || isRouterReady) {
    return getCatalogListingRobots(pageQuery);
  }

  return fallback ?? undefined;
};

export {
  getCatalogListingHref,
  getCatalogPageQuery,
} from '@/lib/shared/lib/listing-page-query';

export const getConciergeItemBreadcrumbs = (
  catalogBreadcrumbs: readonly ConciergeBreadcrumb[],
  catalog: ConciergeItemCatalog,
  itemTitle: string
): ConciergeBreadcrumb[] => {
  const catalogCrumb = catalogBreadcrumbs[catalogBreadcrumbs.length - 1];

  return [
    ...catalogBreadcrumbs.slice(0, -1),
    {
      title: catalogCrumb?.title || catalog,
      link: conciergeItemCatalogPath[catalog],
    },
    { title: itemTitle },
  ];
};
